import React, { useState, useEffect, useRef } from 'react';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import { SiInstagram } from "react-icons/si";
import { FiCopy, FiLink } from "react-icons/fi";
import { FaExternalLinkAlt, FaCheckCircle } from "react-icons/fa";
import { useGet } from '../../../../hooks/useGet';
import api from '../../../../hooks/api';
import { updateBusinessLoginCache } from '../../../../hooks/auth/useBusinessLogin';
import config from '../../../../config';
import './InstagramConnect.css';

const InstagramConnect = ({ businessHook, businessData: propBusinessData, businessId: propBusinessId }) => {
    // استخراج اطلاعات هویتی و هوک
    const effectiveHook = businessHook || {};
    const businessData = propBusinessData || effectiveHook.data || {};
    const businessId = propBusinessId || effectiveHook.businessId || businessData.id;
    const ChatId = effectiveHook.ChatId;
    const currentCollabId = effectiveHook.collabId || null;

    // --- STATE & VARIABLES ---
    const life_time = 120; // 2 دقیقه
    const [time, setTime] = useState(life_time);
    const [timeStart, setTimeStart] = useState(false);
    const [generativeCode, setGenerativeCode] = useState(null);
    const [urlGenerativeCodeCheking, setUrlGenerativeCodeCheking] = useState(null);

    const [instagramUsername, setInstagramUsername] = useState(null);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [pendingData, setPendingData] = useState(null);
    const [isLoadingCode, setIsLoadingCode] = useState(false);

    // لودینگ دکمه‌های تایید / رد
    const [isConfirming, setIsConfirming] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    const isDecisionLoading = isConfirming || isRejecting;

    // جلوگیری از پرش تصویر (Flicker) هنگام آپدیت والد
    const isLocallyConfirmedRef = useRef(false);

    // هوک‌های API
    const { data: data_BusinessCheking } = useGet(urlGenerativeCodeCheking);

    // --- EFFECTS ---

    // 1. مقداردهی اولیه و همگام‌سازی با والد
    useEffect(() => {
        if (businessData) {
            const confirmed = (
                businessData.instagram_confirmed === "1" ||
                businessData.instagram_confirmed === 1 ||
                !!businessData.instagram_verified_at
            );

            if (confirmed) {
                setIsConfirmed(true);
                setInstagramUsername(businessData.instagram_userName);
                // وقتی دیتای جدید از سرور رسید، قفل محلی را باز می‌کنیم
                isLocallyConfirmedRef.current = false; 
            } else {
                // فقط در صورتی وضعیت را فالس کن که خودمان همین الان تاییدش نکرده باشیم
                // این شرط جلوی پرش تصویر (Flicker) را می‌گیرد
                if (!isLocallyConfirmedRef.current) {
                    setIsConfirmed(false);
                    setInstagramUsername(null);
                }
            }
        }
    }, [businessData]);

    // 2. بررسی وضعیت (Polling)
    useEffect(() => {
        if (data_BusinessCheking) {
            const confirmed = (
                data_BusinessCheking.instagram_confirmed === "1" ||
                data_BusinessCheking.instagram_confirmed === 1 ||
                !!data_BusinessCheking.instagram_verified_at
            );

            if (confirmed) {
                setPendingData(data_BusinessCheking);
                setTime(0);
                setUrlGenerativeCodeCheking(null);
            }
        }
    }, [data_BusinessCheking]);

    // 3. تایمر
    useEffect(() => {
        if (timeStart && time > 0 && !isConfirmed && !pendingData) {
            const timer = setInterval(() => {
                setTime((prevTime) => prevTime - 1);
            }, 1000);

            if (time % 15 === 0 && time < life_time) {
                setUrlGenerativeCodeCheking(`business/show/${businessId}`);
            } else {
                setUrlGenerativeCodeCheking(null);
            }

            return () => clearInterval(timer);
        } else if (time === 0) {
            if (!pendingData && !isConfirmed) {
                resetProcess();
            }
        }
    }, [time, timeStart, isConfirmed, pendingData, businessId]);

    // --- FUNCTIONS ---

    const handleStartConnect = () => {
        if (isLoadingCode) return;

        setIsLoadingCode(true);
        setTimeStart(false);
        setGenerativeCode(null);
        setPendingData(null);
        setTime(life_time);

        api.post(`business/active/instagram`, { businessId: businessId })
            .then((response) => {
                if (response.data.success) {
                    const code = response.data.data.instagram_activeCode || response.data.data;
                    setGenerativeCode(code);
                    setTimeStart(true);
                    setUrlGenerativeCodeCheking(null);
                } else {
                    toast.error(response.data.message || "خطا در دریافت کد");
                }
            })
            .catch((error) => {
                console.error(error);
                if (error.response?.data?.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error("خطای ارتباط با سرور");
                }
            })
            .finally(() => {
                setIsLoadingCode(false);
            });
    };

    const resetProcess = () => {
        setTimeStart(false);
        setGenerativeCode(null);
        setPendingData(null);
        setTime(life_time);
        setInstagramUsername(null);
        setUrlGenerativeCodeCheking(null); // متوقف کردن کامل Polling برای جلوگیری از پرش در زمان رد کردن
    };

    const saveConfirmed = async (status) => {
        if (isDecisionLoading || !pendingData) return;

        if (status) {
            setIsConfirming(true);
        } else {
            setIsRejecting(true);
        }

        const url = status ? 'business/instagram/confirmed' : 'business/instagram/rejected';

        try {
            const response = await api.post(url, { businessId: businessId });

            if (response.data.success) {
                if (status) {
                    const verifiedUsername = pendingData.instagram_userName;
                    
                    // فعال کردن قفل برای جلوگیری از تاثیر گرفتن لحظه‌ای از دیتای قدیمی والد
                    isLocallyConfirmedRef.current = true;
                    
                    setIsConfirmed(true);
                    setInstagramUsername(verifiedUsername);
                    setPendingData(null);
                    toast.success(response.data.message || "اتصال با موفقیت انجام شد");

                    // ساخت پی‌لود آپدیت‌شده برای کش
                    const updatedPayload = {
                        ...(businessData || {}),
                        instagram_confirmed: 1,
                        instagram_userName: verifiedUsername,
                        instagram_verified_at: new Date().toISOString()
                    };

                    // بروزرسانی کش رم
                    if (ChatId) {
                        updateBusinessLoginCache(ChatId, currentCollabId, updatedPayload);
                    }

                    // رفرش هوک والد
                    if (typeof effectiveHook?.refetch === 'function') {
                        effectiveHook.refetch();
                    } else if (typeof effectiveHook?.mutate === 'function') {
                        effectiveHook.mutate(updatedPayload, false);
                    }

                    // برادکست رویداد
                    window.dispatchEvent(new CustomEvent('businessInfoUpdated', {
                        detail: {
                            collabId: currentCollabId,
                            businessId: businessId,
                            data: updatedPayload
                        }
                    }));
                } else {
                    resetProcess();
                    toast.info("اتصال توسط شما لغو شد.");
                }
            } else {
                toast.error(response.data.message || "خطایی رخ داد، لطفا مجدد تلاش کنید");
                if (!status) resetProcess();
            }
        } catch (error) {
            toast.error("خطای ارتباط با سرور");
        } finally {
            setIsConfirming(false);
            setIsRejecting(false);
        }
    };

    const fullLink = `${config.api.LinkApp}${instagramUsername || ''}`;

    let statusClass = "empty";
    if (isConfirmed) statusClass = "confirmed";
    else if (pendingData) statusClass = "pending";

    // --- RENDER ---
    return (
        <div className="instagram-box">
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <radialGradient id="ig-gradient" r="150%" cx="30%" cy="107%">
                        <stop stopColor="#fdf497" offset="0%" />
                        <stop stopColor="#fdf497" offset="5%" />
                        <stop stopColor="#fd5949" offset="45%" />
                        <stop stopColor="#d6249f" offset="60%" />
                        <stop stopColor="#285AEB" offset="90%" />
                    </radialGradient>
                </defs>
            </svg>

            {!isConfirmed && !pendingData && !timeStart && (
                <div className="ig-disconnected-container">
                    <div className="ig-icon-wrapper">
                        <SiInstagram className="ig-large-icon" />
                    </div>
                    <h3 className="ig-title">اتصال به اینستاگرام</h3>
                    <p className="ig-subtitle">
                        با اتصال پیج اینستاگرام خود، مشتریان می‌توانند به راحتی شما را پیدا کرده و اعتماد بیشتری به فروشگاه شما داشته باشند.
                    </p>
                    <button
                        className="ig-connect-btn"
                        onClick={handleStartConnect}
                        disabled={isLoadingCode}
                        style={{ opacity: isLoadingCode ? 0.8 : 1, cursor: isLoadingCode ? 'not-allowed' : 'pointer' }}
                    >
                        {isLoadingCode ? (
                            <ReactLoading type={"spin"} color={"#ffffff"} height={24} width={24} />
                        ) : (
                            <>
                                <SiInstagram className="btn-icon" />
                                <span>دریافت کد اتصال</span>
                            </>
                        )}
                    </button>
                </div>
            )}

            {(isConfirmed || pendingData || timeStart) && (
                <div className="instagram-header">
                    <div className='title'>اتصال به اینستاگرام</div>
                    {isConfirmed && (
                        <div className='subtitle'>صفحه اینستاگرامی شما به فروشگاه متصل شده است.</div>
                    )}
                </div>
            )}

            {!isConfirmed && (pendingData || timeStart) && (
                <div className={`instagram-input-box ${statusClass}`}>
                    <SiInstagram
                        className="icon"
                        size={"28px"}
                        style={{ fill: "url(#ig-gradient)" }}
                    />
                    <div className="username">
                        {!businessData ? (
                            <ReactLoading type={"bubbles"} color={"#000"} height={30} width={30} />
                        ) : (
                            pendingData ? pendingData.instagram_userName : (instagramUsername || "در حال شناسایی آیدی شما…")
                        )}
                    </div>
                </div>
            )}

            {isConfirmed && (
                <>
                    <div className="ig-account-card">
                        <div className="account-icon">
                            <SiInstagram size={"55px"} style={{ fill: "url(#ig-gradient)" }} />
                        </div>

                        <div className="account-details">
                            <span className="account-username">{instagramUsername}</span>
                            <div className="account-badge">
                                <FaCheckCircle size="14px" />
                                <span>تایید شده</span>
                            </div>
                        </div>
                    </div>

                    <div className="ig-link-card">
                        <div className="link-label">لینک اختصاصی فروشگاه شما:</div>
                        <div className="link-input-wrapper">
                            <FiLink className="link-icon" size="20px" />
                            <span className="link-url">{fullLink}</span>
                        </div>
                        <div className="link-copy-btn" onClick={() => {
                            navigator.clipboard.writeText(fullLink);
                            toast.success("لینک کپی شد");
                        }}>
                            <span>کپی لینک</span>
                            <FiCopy size={"18px"} />
                        </div>
                    </div>
                </>
            )}

            {!isConfirmed && pendingData && (
                <div className="instagram-confirmed">
                    <div className="question">
                        <div className="description-question">
                            حساب کاربری <span>{pendingData.instagram_userName}</span> متعلق به شماست؟
                        </div>
                        <div className="question-btn">
                            <div 
                                className="yes" 
                                onClick={() => saveConfirmed(true)} 
                                style={{ 
                                    position: 'relative', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    opacity: isDecisionLoading ? 0.7 : 1, 
                                    pointerEvents: isDecisionLoading ? 'none' : 'auto' 
                                }}
                            >
                                <span style={{ opacity: isConfirming ? 0 : 1 }}>بله، تایید می‌کنم</span>
                                
                                {isConfirming && (
                                    <div style={{ position: 'absolute' }}>
                                        <ReactLoading type={"spin"} color={"#ffffff"} height={20} width={20} />
                                    </div>
                                )}
                            </div>
                            
                            <div 
                                className="no" 
                                onClick={() => saveConfirmed(false)} 
                                style={{ 
                                    position: 'relative',
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    opacity: isDecisionLoading ? 0.7 : 1, 
                                    pointerEvents: isDecisionLoading ? 'none' : 'auto' 
                                }}
                            >
                                <span style={{ opacity: isRejecting ? 0 : 1 }}>خیر</span>
                                
                                {isRejecting && (
                                    <div style={{ position: 'absolute' }}>
                                        <ReactLoading type={"spin"} color={"#ffffff"} height={20} width={20} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!isConfirmed && !pendingData && timeStart && generativeCode && (
                <div className="instagram-create-code">
                    <div className='description-wait'>کد زیر را کپی کرده و در دایرکت ارسال کنید:</div>
                    <div className="view-create-code">
                        <div className="timer">{time}</div>
                        <div className="code">{generativeCode}</div>
                        <div className="copy" onClick={() => { navigator.clipboard.writeText(generativeCode); toast.success("کد کپی شد"); }}>
                            <FiCopy size={"24px"} strokeWidth={2} />
                        </div>
                    </div>
                    <a href={`https://ig.me/m/sabad1.shop`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', width: '100%' }}>
                        <div className="btn-instagram-direct">
                            <span>ارسال مستقیم دایرکت</span>
                            <FaExternalLinkAlt size={"14px"} style={{ marginRight: '10px' }} />
                        </div>
                    </a>
                </div>
            )}
        </div>
    );
};

export default InstagramConnect;
